/**
 * The cache fetched whole (unit 25, rule 5): on the first open, and after a
 * reset, `GET /api/changes` gives only a cursor, and each entity is read whole
 * from its own list route, then `fill`ed.
 *
 * **Each entity's filler arrives with its widget** (unit 26A wired the path;
 * 27A brings the first). The compiler holds the table to the contract's
 * entities, so an entity with no filler is a visible `null`, never forgotten:
 * until it has one, its widget is not built and nothing reads its rows.
 */
import { api } from "../api/client";
import { ok } from "../api/query";
import { fill, type Entity, type Rows } from "./cache";
import { announce } from "./outbox";

const PAGE = 200;

export type Filler<E extends Entity> = () => Promise<Rows[E][]>;

export const FILLERS: { [E in Entity]: Filler<E> | null } = {
  // 27A. Each says, for today, what is day-bound on it: a list's `featured`,
  // a todo's `overdue`, a habit's state.
  list: () => ok(api.GET("/api/todos/lists")),
  todo: () => ok(api.GET("/api/todos/items", { params: { query: { all: true } } })),
  habit: () => ok(api.GET("/api/todos/habits", { params: { query: { all: true } } })),
  // 27B. The journal's list is paged (200 at most), so it is read page by page.
  journal: async () => {
    const all: Rows["journal"][] = [];
    for (;;) {
      const page = await ok(
        api.GET("/api/journal", { params: { query: { limit: PAGE, offset: all.length } } }),
      );
      all.push(...page);
      if (page.length < PAGE) return all;
    }
  },
  note: () => ok(api.GET("/api/notes/whole")),
  notebook: async () => (await ok(api.GET("/api/notes/notebooks"))).notebooks,
  // 29A. The pool, and every recipe's cooking (25C3).
  meal: () => ok(api.GET("/api/meal-plan")),
  recipe_cooked: () => ok(api.GET("/api/recipes/cooked")),
  // 28B. Resolved ones too, so Show resolved works with no signal.
  concern: () => ok(api.GET("/api/health/concerns/whole")),
  // 28A. Offline, the dial draws these blocks alone (25C5 Q1).
  turner: () => ok(api.GET("/api/turner/entries")),
  turner_hidden: () => ok(api.GET("/api/turner/hidden-events")),
  // 29B. Every card, so the queue is the cache's (25C6).
  card: () => ok(api.GET("/api/atlas/cards")),
  // 28B. The routine is followed offline (25C7).
  step: () => ok(api.GET("/api/stretching")),
  // 29B. Each active account as the Accounts tab shows it (25C8).
  account: () => ok(api.GET("/api/finance/accounts")),
  // 35A. What a phone keeps to read with no signal; the web reads reading online.
  reading: null,
};

/**
 * Read every entity that has a filler, whole, into the cache — or only those
 * named: a widget reads its own again when it opens and when the day turns,
 * since a day-bound field goes stale with no write (27A). One that fails is
 * left for the next.
 */
export async function fetchWhole(
  db: IDBDatabase,
  fillers = FILLERS,
  only?: readonly Entity[],
): Promise<void> {
  for (const entity of only ?? (Object.keys(fillers) as Entity[])) {
    const filler = fillers[entity] as Filler<typeof entity> | null;
    if (!filler) continue;
    try {
      await fill(db, entity, await filler());
    } catch {
      // No signal, or a refusal: the next reset, or the next open, tries again.
    }
  }
  announce("cache");
}
